/**
 * AUREXIS – Service Health Context
 *
 * Single polling source for all-service health.
 * Previously CommandHeader, CommandSidebar, and CircuitTab each ran their own
 * independent timer which caused them to show different counts simultaneously.
 * MonitoringSettings and SystemControl were never migrated and kept their own
 * naive probes, so the sidebar and the settings monitors disagreed on-screen:
 * one said the fleet was healthy while the other showed services failing.
 *
 * Now: one poll every 15 s. All consumers read the same snapshot.
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SERVICE_REGISTRY, INFRA_SERVICES, TOTAL_SERVICES, type ServiceDefinition } from '@/config/serviceRegistry';
import { setVisibleInterval } from '@/utils/visibleInterval';

/**
 * Organs merged into learning-engine when COGNITIVE_KERNEL_MODE=1.
 *
 * perception is deliberately ABSENT: start_core.sh runs it as its own process in
 * every profile, because a CPU-bound 256-D encoder cannot share the GIL with the
 * surface that has to answer /health. Listing it here (and in brain-monitor) made
 * both report it "up" while it was actually dead for 1d16h after an OOM.
 *
 * meaning-engine and reasoning-engine are absent for the same reason: the
 * cognitive profile runs both standalone as spine stages, so they are judged by
 * their own /health. Only core-brain is collapsed. Because a service must be in
 * this map to borrow the kernel's health, it also caps what a (possibly stale)
 * learning-engine /kernel/status can claim.
 */
const KERNEL_COLLAPSED: Record<string, string> = {
  'core-brain': 'core-brain',
};

const ALL_MONITORED: ServiceDefinition[] = [...SERVICE_REGISTRY, ...INFRA_SERVICES];

export interface ServiceHealthState {
  /** Number of services that responded to their health check */
  servicesOnline: number;
  /** Total number of services in the registry */
  total: number;
  /** Per-service status. STANDBY = down by design in the booted profile (brain-monitor
   *  reads start_core.sh's standby list), not an outage. */
  statuses: Record<string, 'UP' | 'DOWN' | 'PROBING' | 'STANDBY'>;
  /** Per-service latency in ms (-1 when unknown/unreachable) */
  latencies: Record<string, number>;
  /** Per-service human-readable reason, when the prober supplies one */
  reasons: Record<string, string>;
  /** Whether a poll cycle is currently in-flight */
  checking: boolean;
  /** Timestamp of the last completed poll */
  lastCheck: Date | null;
  /** Manually trigger a new poll immediately */
  triggerCheck: () => void;
}

const defaultState: ServiceHealthState = {
  servicesOnline: 0,
  total: TOTAL_SERVICES + INFRA_SERVICES.length,
  statuses: {},
  latencies: {},
  reasons: {},
  checking: true,
  lastCheck: null,
  triggerCheck: () => {},
};

const ServiceHealthContext = createContext<ServiceHealthState>(defaultState);

interface ProbeRow {
  status: 'UP' | 'DOWN' | 'STANDBY';
  latency: number;
  reason: string;
}

export function ServiceHealthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ServiceHealthState>(defaultState);

  // Try the server-side failure-ledger first: brain-monitor probes every service
  // from inside the cluster (accurate latency, no browser CORS/timeout noise, it
  // sees a 503 correctly, and it can TCP-probe the databases the browser cannot).
  // Returns a port->row map, or null if unavailable.
  const fetchServerTopology = async (): Promise<Record<number, ProbeRow> | null> => {
    try {
      const res = await fetch('/svc/brain-monitor/api/v2/monitor/topology', {
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) return null;
      const data = await res.json();
      const byPort: Record<number, ProbeRow> = {};
      for (const s of data.services ?? []) {
        // degraded = still responding (online); only down/unknown is DOWN
        byPort[s.port] = {
          status: s.status === 'standby' ? 'STANDBY'
            : s.status === 'down' || s.status === 'unknown' ? 'DOWN' : 'UP',
          latency: typeof s.latency_ms === 'number' ? Math.round(s.latency_ms) : -1,
          reason: s.reason || '',
        };
      }
      return byPort;
    } catch {
      return null;
    }
  };

  const fetchKernelCollapse = async (): Promise<Set<string>> => {
    try {
      const res = await fetch('/svc/learning/kernel/status', { signal: AbortSignal.timeout(4000) });
      if (!res.ok) return new Set();
      const data = await res.json();
      if (!data.cognitive_kernel) return new Set();
      return new Set<string>(data.collapsed ?? []);
    } catch {
      return new Set();
    }
  };

  const poll = async () => {
    setState((prev) => ({ ...prev, checking: true }));

    const next: Record<string, 'UP' | 'DOWN' | 'PROBING' | 'STANDBY'> = {};
    const lat: Record<string, number> = {};
    const why: Record<string, string> = {};
    ALL_MONITORED.forEach((svc: ServiceDefinition) => { next[svc.id] = 'PROBING'; });

    const serverMap = await fetchServerTopology();
    const collapsed = await fetchKernelCollapse();

    const learningEngineUp = async (): Promise<boolean> => {
      if (serverMap?.[50013]?.status === 'UP') return true;
      try {
        const res = await fetch('/health-proxy/learning-engine', { signal: AbortSignal.timeout(4000) });
        return res.ok;
      } catch {
        return false;
      }
    };
    const leUp = collapsed.size > 0 ? await learningEngineUp() : false;

    let online = 0;
    await Promise.allSettled(
      ALL_MONITORED.map(async (svc: ServiceDefinition) => {
        // Cognitive kernel: collapsed organs inherit LE liveness.
        if (collapsed.size > 0 && KERNEL_COLLAPSED[svc.id] && collapsed.has(KERNEL_COLLAPSED[svc.id]) && leUp) {
          next[svc.id] = 'UP';
          lat[svc.id] = serverMap?.[50013]?.latency ?? -1;
          why[svc.id] = 'collapsed into learning-engine (cognitive kernel)';
          online++;
          return;
        }
        // Prefer server-probed truth when this service's port is covered.
        if (serverMap && svc.port in serverMap) {
          const row = serverMap[svc.port];
          next[svc.id] = row.status;
          lat[svc.id] = row.latency;
          if (row.reason) why[svc.id] = row.reason;
          if (row.status === 'UP') online++;
          return;
        }
        // Fallback: browser-probe (only when brain-monitor itself is unavailable).
        const started = performance.now();
        try {
          const res = await fetch(svc.healthUrl, { signal: AbortSignal.timeout(5000) });
          const elapsed = Math.round(performance.now() - started);
          // A health route that returns the SPA is not a health route. Caddy used
          // to fall /health-proxy/<unknown> through to index.html with HTTP 200, so
          // any service without a route — including one that does not exist —
          // reported UP. Never count an HTML body as health.
          const ct = res.headers.get('content-type') || '';
          const isHtml = ct.includes('text/html');
          lat[svc.id] = elapsed;
          if (res.ok && !isHtml) {
            next[svc.id] = 'UP';
            online++;
          } else {
            next[svc.id] = 'DOWN';
            why[svc.id] = isHtml ? 'no health route (served SPA)' : `HTTP ${res.status}`;
          }
        } catch {
          next[svc.id] = 'DOWN';
          lat[svc.id] = -1;
          why[svc.id] = 'unreachable';
        }
      })
    );

    setState((prev) => ({
      ...prev,
      servicesOnline: online,
      total: TOTAL_SERVICES + INFRA_SERVICES.length,
      statuses: next,
      latencies: lat,
      reasons: why,
      checking: false,
      lastCheck: new Date(),
    }));
  };

  useEffect(() => {
    poll();
    const timer = setVisibleInterval(poll, 15_000);
    return () => timer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: ServiceHealthState = { ...state, triggerCheck: poll };

  return (
    <ServiceHealthContext.Provider value={value}>
      {children}
    </ServiceHealthContext.Provider>
  );
}

export function useServiceHealth(): ServiceHealthState {
  return useContext(ServiceHealthContext);
}
